<template>
  <v-card width="100%" fill-height>
    <v-card-title>
      <v-icon class="mr-1">fad fa-comment-plus</v-icon>
      <v-btn text small @click.native.stop="commenting = true" class="ma-0"> Add a comment</v-btn>
    </v-card-title>
    <v-expand-transition>
      <div v-show="commenting && !LoadingComments">
        <v-col>
          <v-textarea v-model="comment" label="Add a comment" rows="3" filled autoGrow></v-textarea>
        </v-col>
        <v-col xs="12" lg="6" v-if="canSetVisibility">
          <v-list two-line subheader dense>
            <v-subheader>Visibility - who do you want to see this comment?</v-subheader>
            <v-list-item @click="selectVisibility('platform')" :class="{ selected: visibility.platform === true }">
              <v-list-item-avatar>
                <img
                  src="https://s3-eu-west-1.amazonaws.com/media.alternativebusinessfunding.co.uk/public/logo.png"
                  alt="Platform"
                />
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">Platform</v-list-item-title>
                <v-list-item-subtitle>Visible to Platform Users only</v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon v-if="visibility.platform" color="success">fad fa-check</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item @click="selectVisibility('funders')" :class="{ selected: visibility.funders === true }">
              <v-list-item-avatar>
                <v-icon>fad fa-coins</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">Funder</v-list-item-title>
                <v-list-item-subtitle>
                  Visible to Platform Users and any Funder with an enquiry from
                  <span class="font-weight-bold">{{ customer.name }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon v-if="visibility.funders" color="success">fad fa-check</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              @click="selectVisibility('funder', enquiry.id)"
              v-for="enquiry in customer.enquiries"
              :key="enquiry.id"
              :class="{ selected: enquirySelected(enquiry.id) === true }"
            >
              <v-list-item-avatar :color="colour(enquiry.status.name)">
                <span class="headline">{{ startsWith(enquiry.product.provider.name) }}</span>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ enquiry.product.provider.name }}</v-list-item-title>
                <v-list-item-subtitle
                  >Visible to {{ enquiry.product.provider.name }} and Platform Users only</v-list-item-subtitle
                >
              </v-list-item-content>

              <v-list-item-action>
                <v-icon v-if="enquirySelected(enquiry.id)" color="success">fad fa-check</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              v-if="customer.referral"
              @click="selectVisibility('referrer')"
              :class="{ selected: visibility.referrer === true }"
            >
              <v-list-item-avatar color="secondary">
                <span class="white--text headline">{{ startsWith(customer.referrer.name) }}</span>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ customer.referrer.name }}</v-list-item-title>
                <v-list-item-subtitle>Visible to Platform Users and the Referring party only</v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon v-if="visibility.referrer" color="success">fad fa-check</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
        <v-card-actions>
          <v-btn :disabled="cannotPost" :loading="posting" class="px-3" color="accent" @click="postComment">
            <v-icon class="mr-2" color="accent">fad fa-paper-plane</v-icon> Post Comment
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="px-3" color="secondary" text @click="commenting = false">
            <v-icon class="mr-2">fad fa-comment-slash</v-icon> Cancel
          </v-btn>
        </v-card-actions>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';

export default {
  name: 'comment-form',
  props: ['customer'],
  components: {},
  data: () => ({
    comment: null,
    commenting: false,
    posting: false,
    visibility: {
      platform: false,
      funders: false,
      funder: [],
      referrer: false
    }
  }),
  computed: {
    ...mapGetters(['UserHasRole', 'LoadingComments', 'StatusColours', 'FunderEnquiries']),
    canSetVisibility() {
      return this.UserHasRole('Provider') === false && this.UserHasRole('Call Centre') === false;
    },
    cannotPost() {
      if (this.UserHasRole('Provider') || this.UserHasRole('Call Centre') || this.UserHasRole('Property Broker')) {
        return _.isNull(this.comment || this.comment === '');
      }
      return (
        _.isNull(this.comment) ||
        this.comment === '' ||
        (this.visibility.platform === false &&
          this.visibility.funders === false &&
          this.visibility.referrer === false &&
          this.visibility.funder.length < 1)
      );
    }
  },
  methods: {
    ...mapActions(['postCustomerComment']),
    startsWith: function(string) {
      return _.upperCase(string[0]);
    },
    selectVisibility: function(entity, id = '') {
      this.resetVisibility();
      if (entity === 'funder') {
        this.visibility[entity].push(id);
      } else {
        this.visibility[entity] = true;
      }
    },
    resetVisibility: function() {
      this.visibility = {
        platform: false,
        funders: false,
        funder: [],
        referrer: false
      };
    },
    enquirySelected: function(id) {
      return this.visibility.funder.indexOf(id) !== -1;
    },
    postComment() {
      this.posting = true;
      const commentParams = {};

      if (this.UserHasRole('Call Centre') || this.UserHasRole('Property Broker')) {
        this.visibility.platform = true;
      }

      if (this.UserHasRole('Provider')) {
        const enquiry = _.find(this.FunderEnquiries.Active, ['customer.id', this.customer.id]);
        _.merge(commentParams, { id: enquiry.id, type: 'enquiry' });
      } else {
        if (this.visibility.funders === true || this.visibility.platform === true) {
          _.merge(commentParams, { id: this.customer.id, type: 'business' });
        } else if (this.visibility.referrer === true) {
          _.merge(commentParams, { id: this.customer.referral_id, type: 'referral' });
        } else {
          _.merge(commentParams, { id: this.visibility.funder[0], type: 'enquiry' });
        }
      }
      this.postCustomerComment({
        comment: this.comment,
        privacy: this.visibility.platform === true ? 'internal' : 'public',
        commentParams
      }).then(() => {
        this.comment = null;
        this.commenting = false;
        this.posting = false;
      });
    },
    colour(enquiryStatus) {
      let status = _.find(this.StatusColours, ['status', enquiryStatus]);
      if (_.isUndefined(status)) {
        status = { colour: 'light-grey' };
      }
      return status.colour;
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-avatar.light-grey {
  background-color: #f5f5f5;
}
::v-deep .v-textarea {
  background-color: rgba(0, 0, 0, 0.06);
  margin-top: 0;
}
::v-deep .v-input__slot {
  margin-bottom: 0;
  padding-left: 12px;
}
</style>
